import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { prop, has } from 'ramda'
import { useSelector, useMakeSelector } from 'hooks/redux'

import strings from 'utils/strings'

import { updateFragments, resetFragments } from 'actions/powerTakeOff'

import { getInDirtyState, getInErrorState } from 'selectors/validation'
import {
  getDirtyFragments,
  getIsPtoEditBlocked,
  getSelectedPtoGroup,
} from 'selectors/powerTakeOff'
import { makeHasRights } from 'selectors/authorization'
import { UPDATE_PLUS_PTO } from 'constants/authorization'

import { Header } from 'components/patterns'

import SingleEditor from './SingleEditor'
import GroupEditor from './GroupEditor'

import { StyledEditor, StyledDrawerDivider } from './FragmentsListEditor.style'

const FragmentsListEditor = ({ id }) => {
  const { ids, title } = id
  const dispatch = useDispatch()
  const inDirtyState = useSelector(getInDirtyState)
  const inErrorState = useSelector(getInErrorState)
  const dirtyFragments = useSelector(getDirtyFragments)
  const fragments = useSelector(getSelectedPtoGroup, { ids })
  const canUpdatePto = useMakeSelector(makeHasRights, {
    rights: [UPDATE_PLUS_PTO],
  })
  const isPtoEditBlocked = useSelector(getIsPtoEditBlocked)

  const handleSave = useCallback(
    (notes, callback) => {
      dispatch(updateFragments(notes, callback))
    },
    [dispatch]
  )

  const handleReset = useCallback(
    (callback) => {
      const refs = dirtyFragments.map((f) => f.refNo)
      dispatch(resetFragments(refs))
      callback()
    },
    [dispatch, dirtyFragments]
  )

  const listItems = useMemo(() => dirtyFragments.map(prop('name')), [
    dirtyFragments,
  ])

  const getFragmentEditor = useMemo(
    () =>
      fragments.map((fragment) => {
        const isGroupEditor = has('groupId', fragment)
        const { groupId, id: fragmentId } = fragment

        const fragmentEditor = isGroupEditor ? (
          <GroupEditor groupId={groupId} />
        ) : (
          <SingleEditor fragment={fragment} />
        )
        return (
          <React.Fragment key={`${groupId}-${fragmentId}`}>
            {fragmentEditor} <StyledDrawerDivider />
          </React.Fragment>
        )
      }),
    [fragments]
  )

  const itemsWithHeading = [
    {
      items: listItems,
      heading: strings.updated_the_following_settings,
    },
  ]

  return (
    <StyledEditor
      active={canUpdatePto && !isPtoEditBlocked}
      actions={{
        onSave: handleSave,
        onReset: handleReset,
      }}
      overlayContent={{
        save: {
          heading: strings.edit_pto_domain,
          // subheading: domainName,
          lists: itemsWithHeading,
        },
        reset: {
          heading: strings.reset_pto_settings,
          subheading: strings.reset_pto_settings_description,
          lists: itemsWithHeading,
        },
      }}
      hasModifications={inDirtyState}
      hasErrors={inErrorState}
    >
      <Header type="h2">{title}</Header>
      {fragments.length > 0 && getFragmentEditor}
    </StyledEditor>
  )
}

FragmentsListEditor.propTypes = {
  id: PropTypes.object,
}

export default FragmentsListEditor
